<script lang="ts">
import type { HTMLAttributes } from 'vue'
import type { FieldErrorItem } from './FieldError.vue'

export interface FieldErrorTooltipProps {
  id?: string
  errors?: FieldErrorItem[]
  class?: HTMLAttributes['class']
}
</script>

<script setup lang="ts">
import { computed } from 'vue'
import { cn } from '#ui-kit/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '#ui-kit/ui/tooltip'
import { InformationCircleIcon } from '@heroicons/vue/24/outline'

const props = defineProps<FieldErrorTooltipProps>()

const messages = computed(() => {
  const all = (props.errors ?? []).map((error) => (typeof error === 'string' ? error : error?.message))
  return [...new Set(all.filter((message): message is string => !!message))]
})
</script>

<template>
  <span v-if="messages.length" data-slot="field-error-tooltip" :class="cn('inline-flex', props.class)">
    <Tooltip>
      <TooltipTrigger
        type="button"
        aria-label="Invalid value"
        class="flex rounded-sm text-red-600 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
      >
        <InformationCircleIcon class="size-3" aria-hidden="true" />
      </TooltipTrigger>
      <TooltipContent>
        <div class="flex flex-col gap-2 text-xs">
          <span class="text-white">Invalid value</span>
          <span v-for="message in messages" :key="message" class="text-gray-400">{{ message }}</span>
        </div>
      </TooltipContent>
    </Tooltip>
    <span :id="props.id" role="alert" class="sr-only">{{ messages.join('. ') }}</span>
  </span>
</template>
